// src/store/slices/adminLogsSlice.js

import { createSlice } from "@reduxjs/toolkit";
import {
  fetchLogDates,
  fetchLogSummary,
  fetchHipaaLogs,
  fetchEmailLogs,
  searchLogs,
} from "../thunks/adminLogsThunk";

const adminLogsSlice = createSlice({
  name: "adminLogs",
  initialState: {
    // Available log dates
    availableDates: {
      hipaaLogs: [],
      emailLogs: [],
    },
    
    // Current logs display
    logs: [],
    
    // Log summary for dashboard
    summary: null,
    
    // Search results
    searchResults: [],
    
    // Loading and error states
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    
    // UI state
    activeTab: "hipaa", // 'hipaa' | 'email'
    selectedDate: new Date().toISOString().split('T')[0],
    isSearchMode: false,
    
    // Filters
    filters: {
      eventType: '',
      severity: '',
      status: '',
      emailType: '',
      limit: 100,
      offset: 0
    },
    
    // Search parameters
    searchParams: {
      searchTerm: '',
      dateRange: { start: '', end: '' },
      eventType: ''
    },
    
    // Pagination
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
    },
  },
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.logs = []; // Clear logs when switching tabs
    },
    
    setSelectedDate: (state, action) => {
      state.selectedDate = action.payload;
    },
    
    setSearchMode: (state, action) => {
      state.isSearchMode = action.payload;
      if (!action.payload) {
        state.searchResults = [];
      }
    },
    
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    updateSearchParams: (state, action) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    
    clearLogs: (state) => {
      state.logs = [];
      state.searchResults = [];
    },
    
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Log Dates
      .addCase(fetchLogDates.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchLogDates.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.availableDates = action.payload;
      })
      .addCase(fetchLogDates.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch log dates";
      })
      
      // Fetch Log Summary
      .addCase(fetchLogSummary.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchLogSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
      })
      .addCase(fetchLogSummary.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch log summary";
      })
      
      // Fetch HIPAA Logs
      .addCase(fetchHipaaLogs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchHipaaLogs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.logs = action.payload.logs || [];
        state.pagination = {
          currentPage: action.payload.currentPage || 1,
          totalPages: action.payload.totalPages || 1,
          totalCount: action.payload.totalCount || 0,
        };
      })
      .addCase(fetchHipaaLogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch HIPAA logs";
        state.logs = [];
      })
      
      // Fetch Email Logs
      .addCase(fetchEmailLogs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchEmailLogs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.logs = action.payload.logs || [];
        state.pagination = {
          currentPage: action.payload.currentPage || 1,
          totalPages: action.payload.totalPages || 1,
          totalCount: action.payload.totalCount || 0,
        };
      })
      .addCase(fetchEmailLogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch email logs";
        state.logs = [];
      })
      
      // Search Logs
      .addCase(searchLogs.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(searchLogs.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.searchResults = action.payload.results || [];
        state.logs = action.payload.results || []; // Also update logs for display
      })
      .addCase(searchLogs.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to search logs";
        state.searchResults = [];
        state.logs = [];
      });
  },
});

export const {
  setActiveTab,
  setSelectedDate,
  setSearchMode,
  updateFilters,
  updateSearchParams,
  clearLogs,
  clearError,
} = adminLogsSlice.actions;

export default adminLogsSlice.reducer;